/**
 * The Order intake contract — one definition, validated by the API and by the form.
 *
 * The SPA imports the same schemas the `ZodPipe` runs, so a field the browser accepts and
 * the server rejects cannot exist. That is the whole reason this package exists rather than
 * the DTOs living in `apps/api`.
 *
 * ## What the shape says about the domain
 *
 * **There is no postcode field, and there never will be.** §8: neither market runs on
 * reliable street addressing, Lebanese addresses are landmark-based, and *"do not build
 * anything that assumes a postcode exists"*. A nullable postcode column is how that
 * assumption gets in.
 *
 * **The GPS pin is required; every text field is optional.** §8 again — the pin is
 * authoritative, the text advisory. An Order with a pin and nothing else is deliverable; an
 * Order with a full written address and no pin is a guess.
 *
 * **COD is a string of minor units, not a number.** I2 admits no `number` type for money
 * anywhere, and JSON has exactly one numeric type, so the wire format is a decimal string
 * that the API parses into a `bigint`. A JSON number here would round 89 500 000 001 the
 * moment an LBP amount got large, which in LBP is an everyday amount.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
/** Latitude/longitude bounds are the planet's; the operating-area check is the API's. */
export const gpsPinSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
const optionalText = (max) => z.string().trim().min(1).max(max).optional();
/**
 * §8's address model in order: governorate → district → city → zone → landmark →
 * building/floor → GPS pin → directions → contact-at-door.
 *
 * `zone` is deliberately absent from the input: it is *computed* from the pin by
 * point-in-polygon, never accepted from the caller. A Merchant-supplied zone would be a
 * second source of truth for the one thing dispatch geography depends on.
 */
export const addressInputSchema = z.object({
    governorate: optionalText(80),
    district: optionalText(80),
    city: optionalText(80),
    landmark: optionalText(300),
    building: optionalText(120),
    floor: optionalText(40),
    lat: gpsPinSchema.shape.lat,
    lng: gpsPinSchema.shape.lng,
    directions: optionalText(500),
});
/**
 * A dropoff whose pin the caller may not have — ADR-0055.
 *
 * The pickup keeps `addressInputSchema` unchanged: a Merchant knows where its own Seller is,
 * and a warehouse with an approximate location is a different and much worse problem than a
 * Buyer with one. This relaxation is for the **Buyer's** end, where an e-commerce checkout
 * collects a written address because that is what the courier API it is replacing accepted.
 *
 * **Either a pin or a locality, never neither.** `.refine` is what makes that a schema rule
 * rather than a service-layer check: an address with neither is not a partial address, it is
 * an undeliverable one, and the API should say so at the door rather than after a row exists.
 *
 * The pin is still *preferred* and still authoritative when present (§8) — this schema
 * accepts less, it does not accept a pin less readily.
 */
export const dropoffInputSchema = addressInputSchema
    .partial({ lat: true, lng: true })
    .refine((address) => (address.lat === undefined) === (address.lng === undefined), {
    message: 'a pin needs both lat and lng',
    path: ['lat'],
})
    .refine((address) => address.lat !== undefined ||
    address.city !== undefined ||
    address.district !== undefined, {
    message: 'send a pin, or a city or district we can resolve to a Zone',
    path: ['lat'],
});
/** Digits only, with no leading `+` normalisation attempted — see the note below. */
const phoneSchema = z
    .string()
    .trim()
    .min(6)
    .max(32)
    // Deliberately permissive. Lebanese and Syrian numbers are written half a dozen ways and
    // a strict pattern here would reject real Buyers to satisfy a format preference. What
    // matters for I1 is where this value *goes* — `contact_details`, encrypted, never an
    // event payload — not whether it matched a regex on the way in.
    .regex(/^[+0-9][0-9 ()\-.]*$/u, 'phone_format');
/**
 * One declared piece — what the Merchant says it is handing over (migration 0025).
 *
 * **Every measure is optional**, and that is the compatibility guarantee rather than
 * laxness: the live integration declares a count and nothing else, and a required weight
 * would refuse every Order it places. A piece with no weight prices into the tariff's first
 * band, which on a flat wildcard card is the fee that Merchant already pays.
 *
 * **Dimensions are all-or-nothing.** Two of three sides cannot compute a volume, and a
 * partial declaration priced as though nothing arrived is the silent kind of wrong. The
 * database says the same thing in `order_parcels_dimensions_complete`; this says it at the
 * door, where the caller can still fix it.
 *
 * Integers, not decimals: grams and millimetres, so there is no float in a quantity that
 * multiplies into a price (I2's argument applied to measures rather than to money).
 *
 * Both bounds are sanity limits rather than ruled ones — a tonne and three metres are past
 * anything a Courier carries to a door, and a value beyond them is a caller's unit bug
 * (kilograms sent as grams, metres sent as millimetres) reaching the database.
 */
export const parcelInputSchema = z
    .object({
    weightGrams: z.number().int().min(1).max(1_000_000).optional(),
    lengthMm: z.number().int().min(1).max(3_000).optional(),
    widthMm: z.number().int().min(1).max(3_000).optional(),
    heightMm: z.number().int().min(1).max(3_000).optional(),
})
    .refine((parcel) => [parcel.lengthMm, parcel.widthMm, parcel.heightMm].every((side) => side === undefined) ||
    [parcel.lengthMm, parcel.widthMm, parcel.heightMm].every((side) => side !== undefined), { message: 'send all three dimensions or none', path: ['lengthMm'] });
/** Integer minor units as a string (I2), in the Order's own `codCurrency`. */
const declaredValueSchema = z.string().regex(/^\d{1,18}$/u, 'money_minor_units');
/**
 * The two ways to say how many pieces, and why both survive.
 *
 * `parcelCount` is what the published integration contract asks for and what the live
 * Merchant sends. `parcels` is what the rate engine needs. Accepting either keeps that
 * integration working unchanged; accepting **both at once** would put the count in two
 * places, so a body carrying both is refused rather than reconciled — there is no correct
 * answer when they disagree, and picking one silently is how they come to disagree.
 */
const parcelDeclaration = (body) => body.parcelCount === undefined || body.parcels === undefined;
export const createOrderSchema = z.object({
    /** The Merchant's own reference. Ours is minted; theirs is for their books. */
    merchantRef: optionalText(80),
    itemDescription: z.string().trim().min(1).max(500),
    goodsCategory: z.string().trim().min(1).max(64),
    /** Integer minor units as a string (I2). `-` is rejected: a negative COD is not an Order. */
    codAmountMinor: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
    codCurrency: z.enum(['USD', 'LBP', 'SYP']),
    pickup: addressInputSchema,
    dropoff: dropoffInputSchema,
    /**
     * Buyer contact. Sent once, stored encrypted, and **never returned** — no Merchant-facing
     * response carries these back, which is why the detail screen has no contact section at
     * all rather than an empty one (§4).
     */
    buyerName: z.string().trim().min(1).max(200),
    buyerPhone: phoneSchema,
    buyerContactAtDoor: optionalText(200),
    /**
     * How many barcoded Parcels this Order ships as (§3: one Shipment carries **one or more**).
     *
     * **The count with no measures.** Migration 0023 chose this over an array because nothing
     * read a weight; the rate engine now does, so `parcels` below is the fuller declaration
     * and this stays for the callers already using it. Send one or the other, never both.
     *
     * Optional, because 1 is what every Order has always been rather than a guess.
     */
    parcelCount: z.number().int().min(1).max(20).optional(),
    /**
     * The same declaration with measures — one element per piece, in the order they are handed
     * over. Length is the count, so a two-element array is a two-Parcel Order.
     *
     * The upper bound matches `parcelCount`'s for the same reason: 20 barcodes is already more
     * than a Courier carries for one Buyer, and a four-digit array is a caller's bug.
     */
    parcels: z.array(parcelInputSchema).min(1).max(20).optional(),
    /**
     * What the goods are worth, which is not what the Buyer pays.
     *
     * Feeds an insurance surcharge, and is the only value a prepaid shipment has to take one
     * against — its COD amount is zero by definition. Optional: absent means undeclared, which
     * is different from worthless, so nothing defaults it to `0`.
     */
    declaredValueMinor: declaredValueSchema.optional(),
}).refine(parcelDeclaration, {
    message: 'send parcelCount or parcels, not both',
    path: ['parcels'],
});
/**
 * ADR-0072. The Merchant's own texts come back on the Order view; the address texts and the Buyer's
 * contact reach Ops (the shipment detail) and the Courier (the working set).
 */
export const createOrderRendered = renderedText(createOrderSchema, {
    prose: [
        'itemDescription',
        'pickup.governorate',
        'pickup.district',
        'pickup.city',
        'pickup.landmark',
        'pickup.building',
        'pickup.floor',
        'dropoff.governorate',
        'dropoff.district',
        'dropoff.city',
        'dropoff.landmark',
        'dropoff.building',
        'dropoff.floor',
        'dropoff.directions',
        'buyerName',
        'buyerContactAtDoor',
    ],
    refs: ['merchantRef'],
    exempt: {
        goodsCategory: 'a catalogue code: one not in the catalogue is refused (order_category_unknown) before any write, ' +
            'and a code is [a-z][a-z0-9_]*, which no phone pattern can match',
        codAmountMinor: 'minor units, digits only, stored as a BIGINT: a view shows the integer, with no + and no leading 0',
        declaredValueMinor: 'minor units, digits only, stored as a BIGINT: a view shows the integer, with no + and no leading 0',
        buyerPhone: "the Buyer's phone itself: sealed in contact_details and never rendered",
        'pickup.directions': 'accepted and discarded: only dropoff.directions is stored, with the Buyer contact',
    },
});
/**
 * What an Order would cost, before there is an Order.
 *
 * **A pin pair, a category and an amount — and deliberately nothing else.** A checkout
 * pricing a cart holds the Buyer's name and phone; sending them here would put Buyer PII on
 * a request that writes nothing, keeps nothing, and has no `contact_details` row to put it
 * in. The narrowest input that can answer the question is the whole design.
 *
 * The pins are the same two the Order will carry, because the answer depends on which Zones
 * they fall in — the fee is per service class, and the service class is whether the two
 * Zones share a City Cluster (§5).
 */
/**
 * A quote's dropoff — the same either-or as an Order's, and narrower than an address.
 *
 * No landmark, building or floor: a quote is priced from the Zone pair, and those three
 * change nothing about the answer. A field a request does not use is a field somebody
 * eventually populates with a Buyer's door.
 */
export const quoteDropoffSchema = z
    .object({
    lat: gpsPinSchema.shape.lat.optional(),
    lng: gpsPinSchema.shape.lng.optional(),
    governorate: optionalText(80),
    district: optionalText(80),
    city: optionalText(80),
})
    .refine((address) => (address.lat === undefined) === (address.lng === undefined), {
    message: 'a pin needs both lat and lng',
    path: ['lat'],
})
    .refine((address) => address.lat !== undefined || address.city !== undefined || address.district !== undefined, {
    message: 'send a pin, or a city or district we can resolve to a Zone',
    path: ['lat'],
});
export const quoteOrderSchema = z
    .object({
    goodsCategory: z.string().trim().min(1).max(64),
    codAmountMinor: z.string().regex(/^\d{1,18}$/u, 'money_minor_units'),
    codCurrency: z.enum(['USD', 'LBP', 'SYP']),
    pickup: gpsPinSchema,
    dropoff: quoteDropoffSchema,
    /**
     * The same two ways of declaring pieces the Order takes, and for a load-bearing reason:
     * a quote that could not describe what an Order describes would answer a different
     * question from the one the basket is about to ask, and the shopper would be shown a
     * price the Order then does not honour.
     */
    parcelCount: z.number().int().min(1).max(20).optional(),
    parcels: z.array(parcelInputSchema).min(1).max(20).optional(),
    declaredValueMinor: declaredValueSchema.optional(),
})
    .refine(parcelDeclaration, {
    message: 'send parcelCount or parcels, not both',
    path: ['parcels'],
});
/** A quote writes nothing, and answers with Zone ids and a price: none of its text is shown back. */
export const quoteOrderRendered = renderedText(quoteOrderSchema, {
    exempt: {
        goodsCategory: 'a catalogue code, refused unless the catalogue holds it; a quote stores and echoes nothing',
        codAmountMinor: 'minor units, digits only; a quote stores nothing and prices from the parsed integer',
        declaredValueMinor: 'minor units, digits only; a quote stores nothing and prices from the parsed integer',
        'dropoff.governorate': 'matched against Zone names to price the quote; nothing is stored or echoed',
        'dropoff.district': 'matched against Zone names to price the quote; nothing is stored or echoed',
        'dropoff.city': 'matched against Zone names to price the quote; nothing is stored or echoed',
    },
});
export const cancelOrderSchema = z.object({
    /** Table 1a: "decline carries reason code". A cancellation is a decision, so it has one. */
    reasonCode: z.string().trim().min(1).max(64),
});
/**
 * Shown as the event's reason code on the Merchant's and the Ops timeline. A reference, not prose:
 * a Merchant's system sends a code, and "PO 71123456" is a legitimate one.
 */
export const cancelOrderRendered = renderedText(cancelOrderSchema, { refs: ['reasonCode'] });
export const listOrdersSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const createMerchantSchema = z.object({
    displayNameAr: z.string().trim().min(1).max(200),
    displayNameEn: z.string().trim().min(1).max(200).optional(),
    ikigaiTenantId: z.string().trim().min(1).max(120).optional(),
    /** The screening subject (I7) — the legal entity, which need not be the display name. */
    legalName: z.string().trim().min(1).max(300),
    /**
     * The legal name as written in Latin letters, screened beside `legalName` (SC2b, ADR-0071 §12):
     * an Arabic legal name the list covers only in part may clear beside a Latin name the list covers
     * completely. Optional. The service refuses a value that does not read as Latin script.
     */
    legalNameLatin: z.string().trim().min(1).max(300).optional(),
    country: z.enum(['LB', 'SY']),
});
export const createMerchantRendered = renderedText(createMerchantSchema, {
    prose: ['displayNameAr', 'displayNameEn'],
    exempt: {
        legalName: 'checked by MerchantsService with its own code, legal_name_phone_shaped (ADR-0071)',
        legalNameLatin: 'checked by MerchantsService with legalName’s codes, and read as Latin script (SC2b)',
        ikigaiTenantId: 'stored only in merchants.ikigai_tenant_id; no view, event or message reads it back',
    },
});
/** The statuses a Merchant screen renders. Mirrors the machine's 1a states, not all 29. */
export const MERCHANT_VISIBLE_STATUSES = [
    'Draft',
    'Accepted',
    'CancelledPreAcceptance',
    'CancelledPrePickup',
];
/**
 * Minting a Merchant's machine credential (ADR-0023).
 *
 * The label is required and bounded: an operator revoking the right key months later has
 * only this and an opaque session id to go on, so an empty one is refused rather than
 * defaulted — a list of five keys all called "default" is the same as a list of none.
 */
/**
 * Amend a Merchant's identifying facts — T2-10, owed since migration 0015.
 *
 * Two kinds of field in one body, and they behave differently on purpose. A **display
 * name** is cosmetic. A **legal name or country** is the thing §7 screens and the thing a
 * corridor ruleset keys on, so changing either means the Merchant's existing screening was
 * of a different entity — see `MerchantsService.amend`.
 *
 * `.refine` requires at least one field: a PATCH with an empty body is a request that means
 * nothing, and answering 200 to it would write an event recording no change.
 */
export const amendMerchantSchema = z
    .object({
    displayNameAr: z.string().trim().min(1).max(200).optional(),
    displayNameEn: z.string().trim().min(1).max(200).optional(),
    legalName: z.string().trim().min(1).max(300).optional(),
    /**
     * Absent leaves it alone, a string sets it, and `null` clears it (SC2b). Either change moves the
     * screening subject, exactly as a legal-name change does.
     */
    legalNameLatin: z.string().trim().min(1).max(300).nullable().optional(),
    country: z.enum(['LB', 'SY']).optional(),
    /** Why. Free text, because an amendment is a human act and the reason is evidence. */
    note: z.string().trim().min(1).max(500),
})
    .refine((body) => body.displayNameAr !== undefined ||
    body.displayNameEn !== undefined ||
    body.legalName !== undefined ||
    // `null` is a change — it clears the Latin name — so presence is what counts, not truthiness.
    body.legalNameLatin !== undefined ||
    body.country !== undefined, { message: 'at least one field must change' });
export const amendMerchantRendered = renderedText(amendMerchantSchema, {
    prose: ['displayNameAr', 'displayNameEn'],
    exempt: {
        legalName: 'checked by MerchantsService with its own code, legal_name_phone_shaped (ADR-0071)',
        legalNameLatin: 'checked by MerchantsService with legalName’s codes, and read as Latin script (SC2b)',
        note: 'stored only in the merchant.amended event payload; no view renders it',
    },
});
export const issueApiKeySchema = z.object({
    label: z.string().trim().min(1).max(60),
});
/** Shown on the mint response and on the credentials list. */
export const issueApiKeyRendered = renderedText(issueApiKeySchema, { prose: ['label'] });
//# sourceMappingURL=orders.js.map